import React from 'react';
import { Tabs, message, Button, Modal } from 'antd';
import type { TabsProps } from 'antd';
import { CheckCircleFilled, CopyOutlined } from '@ant-design/icons';
import s from './s.module.less';
import type { DashboardTab, FaxContact } from 'types/eFax';
import ReceivedFaxTab from './ReceivedFaxTab';
import SentFaxTab from './SentFaxTab';
import ContactsTab from './ContactsTab';
import CoverSheetTab from './CoverSheetTab';
import SendFaxDrawer from './SendFaxDrawer';
import CancelEfaxServiceModal from './CancelEfaxServiceModal';
import AddContactModal from './AddContactModal';
import EfaxSettingsDrawer from './EfaxSettingsDrawer';
import EFaxLandingPage from '../EFaxLandingPage';
import useFaxNumber from 'hooks/useFaxNumber';
import ExclamationIcon from 'assets/figma/efax/efax-exclamation.svg';
import { getHasHideFaxNumberBanner, setHasHideFaxNumberBanner } from 'utils/localstore';
import {
    EFAX_USER_CANCELED_SESSION_KEY,
    isEfaxSubscriptionCanceledStatus,
} from 'utils/eFaxSubscriptionStatus';
import { EFAX_PROVISION_SESSION_KEY } from 'types/eFax';
import { faxContactCreate, faxContactDelete, faxContactList, faxContactUpdate, faxNumberCancel, faxInboxUnreadCount } from 'api/fax';
import {
    extractFaxContactListPage,
    isFaxScheduleSuccess,
    mapFaxContactVoToFaxContact,
} from 'utils/eFaxScheduleRows';

type Props = {
    /** Optional hook for future canceled-state CTA. */
    onGetStarted?: () => void;
};

const EFaxDashboardPage = (props: Props) => {
    const { onGetStarted } = props;
    const [activeTab, setActiveTab] = React.useState<DashboardTab>('received');
    const [receivedRefreshKey, setReceivedRefreshKey] = React.useState(0);
    const [sentRefreshKey, setSentRefreshKey] = React.useState(0);
    const [showSendFax, setShowSendFax] = React.useState(false);
    const [showBanner, setShowBanner] = React.useState(!getHasHideFaxNumberBanner());
    const [contacts, setContacts] = React.useState<FaxContact[]>([]);
    const [contactsLoading, setContactsLoading] = React.useState(false);
    const [unreadCount, setUnreadCount] = React.useState(0);
    const [faxNumber, , refetchFaxNumber, { hasCancelledAccount }] = useFaxNumber();
    const [showSettings, setShowSettings] = React.useState(false);
    const [showCancelModal, setShowCancelModal] = React.useState(false);
    const [cancelSubmitting, setCancelSubmitting] = React.useState(false);
    const [userCanceledLocal, setUserCanceledLocal] = React.useState(
        () => sessionStorage.getItem(EFAX_USER_CANCELED_SESSION_KEY) === '1',
    );
    const [showLearnMore, setShowLearnMore] = React.useState(false);
    const [quickAddFaxNumber, setQuickAddFaxNumber] = React.useState<string | undefined>(undefined);
    const [showQuickAddContact, setShowQuickAddContact] = React.useState(false);

    const faxDisplay = faxNumber?.faxNumberDisplay || faxNumber?.faxNumber || '';
    const faxCopyRaw = faxNumber?.faxNumber || '';

    // With `activeAccount` (faxNumber), rely on subscription status / local cancel only — not `hasCancelledAccount`
    // (no activeAccount + cancelled is handled when `faxNumber` is empty via `hasCancelledAccount`).
    const isCanceled = faxNumber
        ? isEfaxSubscriptionCanceledStatus(faxNumber?.status) || userCanceledLocal
        : hasCancelledAccount || userCanceledLocal;

    const fetchContacts = React.useCallback(async (opts?: { silent?: boolean }) => {
        if (isCanceled) return;
        const silent = opts?.silent ?? false;
        if (!silent) setContactsLoading(true);
        try {
            const result = await faxContactList({ current: 1, size: 200 });
            const body = result?.data;
            if (!result?.error && body) {
                const { records } = extractFaxContactListPage(body);
                setContacts(records.map(mapFaxContactVoToFaxContact));
            } else if (result?.error) {
                message.error(
                    typeof result.error === 'string' ? result.error : 'Failed to load contacts',
                );
            }
        } finally {
            if (!silent) setContactsLoading(false);
        }
    }, [isCanceled]);

    React.useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    const fetchUnreadCount = React.useCallback(async () => {
        if (isCanceled) return;
        const result = await faxInboxUnreadCount();
        const body = result?.data as { code?: number; data?: number } | undefined;
        if (!result?.error && body && isFaxScheduleSuccess(body)) {
            setUnreadCount(body.data ?? 0);
        }
    }, [isCanceled]);

    React.useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    const handleCopyNumber = () => {
        if (!faxCopyRaw) return;
        navigator.clipboard
            .writeText(faxCopyRaw)
            .then(() => {
                message.success('Fax number copied!');
            })
            .catch(() => {
                message.error('Failed to copy fax number');
            });
    };

    const handleAddContact = async (contact: FaxContact) => {
        const notesTrimmed = contact.notes?.trim();
        const result = await faxContactCreate({
            faxNumber: contact.faxNumber.trim(),
            name: contact.name.trim(),
            isFavorite: false,
            ...(notesTrimmed ? { notes: notesTrimmed } : {}),
            tag: contact.tag ? [contact.tag] : [],
        });
        const envelope = result?.data as { code?: number; message?: string; data?: unknown } | undefined;
        if (result?.error || !envelope || !isFaxScheduleSuccess(envelope)) {
            const errText =
                envelope?.message || (typeof result?.error === 'string' ? result.error : null) || 'Failed to add contact';
            message.error(errText);
            throw new Error(errText);
        }
        await fetchContacts({ silent: true });
        message.success('Contact added');
    };

    const handleDeleteContact = async (id: string) => {
        const contactId = Number(id);
        if (!Number.isFinite(contactId)) {
            const errText = 'Invalid contact id';
            message.error(errText);
            throw new Error(errText);
        }
        const result = await faxContactDelete(contactId);
        const envelope = result?.data as { code?: number; message?: string } | undefined;
        const okNoContent = !result?.error && !envelope;
        const okEnvelope = !!envelope && isFaxScheduleSuccess(envelope);
        if (!okNoContent && !okEnvelope) {
            const errText =
                envelope?.message ||
                (typeof result?.error === 'string' ? result.error : null) ||
                'Failed to delete contact';
            message.error(errText);
            throw new Error(errText);
        }
        await fetchContacts({ silent: true });
    };

    const handleUpdateContact = async (updated: FaxContact) => {
        const contactId = Number(updated.id);
        if (!Number.isFinite(contactId)) {
            const errText = 'Invalid contact id';
            message.error(errText);
            throw new Error(errText);
        }
        const notesTrimmed = updated.notes?.trim();
        const result = await faxContactUpdate(contactId, {
            faxNumber: updated.faxNumber.trim(),
            name: updated.name.trim(),
            isFavorite: false,
            ...(notesTrimmed ? { notes: notesTrimmed } : {}),
            tag: updated.tag ? [updated.tag] : [],
        });
        const envelope = result?.data as { code?: number; message?: string; data?: unknown } | undefined;
        if (result?.error || !envelope || !isFaxScheduleSuccess(envelope)) {
            const errText =
                envelope?.message ||
                (typeof result?.error === 'string' ? result.error : null) ||
                'Failed to update contact';
            message.error(errText);
            throw new Error(errText);
        }
        await fetchContacts({ silent: true });
    };

    const handleQuickAddContact = (faxNum: string) => {
        setQuickAddFaxNumber(faxNum);
        setShowQuickAddContact(true);
    };

    const handleConfirmCancelService = async () => {
        setCancelSubmitting(true);
        try {
            const result = await faxNumberCancel();
            const envelope = result?.data as { code?: number; message?: string } | undefined;
            const okNoContent = !result?.error && !envelope;
            const okEnvelope = !!envelope && isFaxScheduleSuccess(envelope);
            if (!okNoContent && !okEnvelope) {
                const errText =
                    envelope?.message ||
                    (typeof result?.error === 'string' ? result.error : null) ||
                    'Failed to cancel eFax service';
                message.error(errText);
                return;
            }
            sessionStorage.setItem(EFAX_USER_CANCELED_SESSION_KEY, '1');
            // Clear any optimistic "provisioned" number cached in session.
            // Otherwise, cancel -> re-purchase can mistakenly reuse stale data and skip 选号 flow.
            sessionStorage.removeItem(EFAX_PROVISION_SESSION_KEY);
            setUserCanceledLocal(true);
            message.success('Your eFax service has been cancelled.');
            setShowCancelModal(false);
            setShowSettings(false);
            refetchFaxNumber();
        } finally {
            setCancelSubmitting(false);
        }
    };

    const tabItems: TabsProps['items'] = [
        {
            key: 'received',
            label: (
                <span>
                    Received fax
                    {unreadCount > 0 && (
                        <span
                            style={{
                                background: '#DC2626',
                                color: '#fff',
                                borderRadius: 10,
                                padding: '1px 6px',
                                fontSize: 12,
                                fontWeight: 500,
                                marginLeft: 6,
                            }}
                        >
                            {unreadCount}
                        </span>
                    )}
                </span>
            ),
            children: <ReceivedFaxTab contacts={contacts} onUnreadCountChange={fetchUnreadCount} onAddContact={handleQuickAddContact} refreshKey={receivedRefreshKey} />,
        },
        {
            key: 'sent',
            label: 'Sent fax',
            children: <SentFaxTab contacts={contacts} onAddContact={handleQuickAddContact} refreshKey={sentRefreshKey} />,
        },
        {
            key: 'contacts',
            label: 'Contacts',
            children: (
                <ContactsTab
                    contacts={contacts}
                    loading={contactsLoading}
                    onAdd={handleAddContact}
                    onDelete={handleDeleteContact}
                    onUpdate={handleUpdateContact}
                />
            ),
        },
        {
            key: 'templates',
            label: 'Cover sheet templates',
            children: <CoverSheetTab />,
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.titleBar}>
                <div className={s.pageTitle}>eFax</div>
                <div className={s.actions}>
                    <button type="button" className={s.headerBtn} onClick={() => setShowLearnMore(true)}>
                        Learn more
                    </button>
                    {!isCanceled && (
                        <>
                            <button type="button" className={s.headerBtn} onClick={handleCopyNumber}>
                                {faxDisplay}
                                <CopyOutlined className={s.copyIcon} />
                            </button>
                            <button type="button" className={s.headerBtn} onClick={() => setShowSettings(true)}>
                                Settings
                            </button>
                        </>
                    )}
                    <Button
                        type="primary"
                        className={`${s.sendFaxBtn} ${isCanceled ? s.sendFaxBtnDisabled : ''}`}
                        disabled={isCanceled}
                        onClick={() => !isCanceled && setShowSendFax(true)}
                    >
                        Send Fax
                    </Button>
                </div>
            </div>

            {isCanceled && (
                <div className={s.canceledBanner}>
                    <img src={ExclamationIcon} className={s.canceledBannerIcon} alt="" aria-hidden="true" />
                    <div className={s.canceledBannerContent}>
                        <div className={s.canceledBannerTitle}>
                            Your eFax service has been canceled. You can enable it again anytime to send and receive faxes within Klarity.
                        </div>
                        <div className={s.canceledBannerDesc}>
                            Starting at $9.90/month per fax number, 250 pages included per month.
                        </div>
                        <div className={s.canceledBannerActions}>
                            <Button
                                type="primary"
                                className={s.canceledBannerPrimary}
                                onClick={() => onGetStarted?.()}
                                disabled={!onGetStarted}
                            >
                                Get started
                            </Button>
                            <Button className={s.canceledBannerSecondary} onClick={() => setShowLearnMore(true)}>
                                Learn more
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {showBanner && !isCanceled && (
                <div className={s.successBanner}>
                    <CheckCircleFilled className={s.bannerIcon} />
                    <div className={s.bannerContent}>
                        <div className={s.bannerTitle}>
                            Congratulations! Your fax number <strong>{faxDisplay}</strong> is active.
                        </div>
                        <div className={s.bannerDesc}>You can now send and receive faxes inside Klarity.</div>
                    </div>
                    <button
                        type="button"
                        className={s.bannerDismiss}
                        onClick={() => {
                            setHasHideFaxNumberBanner(true);
                            setShowBanner(false);
                        }}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            <Tabs
                activeKey={activeTab}
                items={tabItems}
                onChange={(key) => {
                    const tab = key as DashboardTab;
                    setActiveTab(tab);
                    if (tab === 'received') setReceivedRefreshKey((k) => k + 1);
                    else if (tab === 'sent') setSentRefreshKey((k) => k + 1);
                }}
            />

            <SendFaxDrawer
                open={showSendFax}
                onClose={() => setShowSendFax(false)}
                contacts={contacts}
                onSendSuccess={() => setSentRefreshKey((k) => k + 1)}
                onContactsChanged={() => {
                    fetchContacts({ silent: true }).catch(() => undefined);
                }}
            />

            <EfaxSettingsDrawer
                open={showSettings}
                onClose={() => setShowSettings(false)}
                faxNumber={faxNumber}
                onRequestCancelService={() => {
                    setShowSettings(false);
                    setShowCancelModal(true);
                }}
            />

            <CancelEfaxServiceModal
                open={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleConfirmCancelService}
                confirming={cancelSubmitting}
            />

            <AddContactModal
                open={showQuickAddContact}
                onClose={() => {
                    setShowQuickAddContact(false);
                    setQuickAddFaxNumber(undefined);
                }}
                onSave={async (contact) => {
                    await handleAddContact(contact);
                    setShowQuickAddContact(false);
                    setQuickAddFaxNumber(undefined);
                }}
                initialFaxNumber={quickAddFaxNumber}
            />

            <Modal
                open={showLearnMore}
                onCancel={() => setShowLearnMore(false)}
                footer={null}
                title="Learn more about eFax"
                width={900}
                bodyStyle={{ padding: 0 }}
                className={s.learnMoreModal}
                destroyOnClose
            >
                <EFaxLandingPage isModal />
            </Modal>
        </div>
    );
};

export default EFaxDashboardPage;
